import { TokensDto, UserDto } from '../commonTypes/user.types';
import { diContainer } from '../libs/container';
import { ApiBaseClass, IServiceHttp } from './ApiBase';

export namespace AuthApiRequest {
    export interface Auth {}

    export interface AuthGoogle {
        body: {
            refreshToken: string;
        };
    }

    export interface AuthMicrosoft {
        body: {
            idToken: string;
        };
    }

    export interface AuthSlack {
        body: {
            accessToken: string;
            idToken: string;
        };
    }

    export interface SignIn {
        body: {
            username: string;
            password: string;
        };
    }

    export interface ForgotPassword {
        body: {
            username: string;
        };
    }

    export interface ConfirmForgotPassword {
        body: {
            username: string;
            confirmationCode: string;
            password: string;
        };
    }

    export interface ConfirmSignUp {
        body: {
            email: string;
            confirmationCode: string;
        };
    }

    export interface SignUp {
        body: {
            email: string;
            password: string;
            givenName: string;
            familyName: string;
        };
    }

    export interface SignOut {}

    export interface RefreshToken {
        body: {
            refreshToken: string;
        };
    }

    export interface ResendConfirmationCode {
        body: {
            email: string;
        };
    }
}

export namespace AuthApiResponse {
    export interface Auth {
        body: UserDto;
    }

    export interface AuthGoogle {
        body: {
            user: UserDto;
            tokens?: TokensDto;
        };
    }

    export interface AuthMicrosoft {
        body: {
            user: UserDto;
            tokens?: TokensDto;
        };
    }

    export interface AuthSlack {
        body: {
            user: UserDto;
            tokens?: TokensDto;
        };
    }

    export interface SignIn {
        body: {
            user: UserDto;
            tokens: TokensDto;
        };
    }

    export interface RefreshToken {
        body: TokensDto;
    }
}

export class AuthApi {
    constructor(private readonly apiBase: ApiBaseClass) {}

    public auth(rest: IServiceHttp<AuthApiRequest.Auth, AuthApiResponse.Auth>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/auth',
            isAuthorized: true,
            loaderType: 'authLoader',
        });
    }

    public authNoRoleCheck(rest: IServiceHttp<AuthApiRequest.Auth, AuthApiResponse.Auth>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/auth-no-role-check',
            isAuthorized: true,
            loaderType: 'authLoader',
        });
    }

    public authGoogle(rest: IServiceHttp<AuthApiRequest.AuthGoogle, AuthApiResponse.AuthGoogle>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/google-auth',
            isAuthorized: true,
            loaderType: 'authLoader',
        });
    }

    public authSlack(rest: IServiceHttp<AuthApiRequest.AuthSlack, AuthApiResponse.AuthSlack>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/slack-auth',
            isAuthorized: false,
            loaderType: 'authLoader',
        });
    }

    public authMicrosoft(rest: IServiceHttp<AuthApiRequest.AuthMicrosoft, AuthApiResponse.AuthMicrosoft>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/microsoft-auth',
            isAuthorized: false,
            loaderType: 'authLoader',
        });
    }

    public signIn(rest: IServiceHttp<AuthApiRequest.SignIn, AuthApiResponse.SignIn>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/sign-in',
            isAuthorized: false,
        });
    }

    public forgotPassword(rest: IServiceHttp<AuthApiRequest.ForgotPassword, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/forgot-password',
            isAuthorized: false,
        });
    }

    public confirmForgotPassword(rest: IServiceHttp<AuthApiRequest.ConfirmForgotPassword, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/confirm-forgot-password',
            isAuthorized: false,
        });
    }

    public confirmSignUp(rest: IServiceHttp<AuthApiRequest.ConfirmSignUp, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/confirm-sign-up',
            isAuthorized: false,
        });
    }

    public signUp(rest: IServiceHttp<AuthApiRequest.SignUp, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/sign-up',
            isAuthorized: false,
        });
    }

    public signOut(rest: IServiceHttp<AuthApiRequest.SignOut, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/sign-out',
            isAuthorized: true,
        });
    }

    public refreshToken(rest: IServiceHttp<AuthApiRequest.RefreshToken, AuthApiResponse.RefreshToken>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/refresh-token',
            isAuthorized: false,
        });
    }

    public resendConfirmationCode(rest: IServiceHttp<AuthApiRequest.ResendConfirmationCode, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/resend-confirmation-code',
            isAuthorized: false,
        });
    }
}

diContainer.bindAsDynamic(AuthApi, c => new AuthApi(c.get(ApiBaseClass)));
